import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, switchMap } from 'rxjs';
import { AspectCollection } from '@lib.core/entities/indicator/indicator';
import { ProfileOverview } from '@lib.core/entities/profile/profile-overview';
import { ProfileAspectCollection } from '@lib.core/entities/profile/profile-aspect-collection';
import { getHttpParams } from '@lib.resources/utils/url';
import { ProfileResource } from '@lib.resources/profile/resource';
import { ResourceRegistry } from '@lib.resources/resource-registry/resource-registry';
import { EnvironmentResource } from '@lib.resources/environment/resource';
import { newAspectCollectionFromDto, AspectCollectionDTO } from '@lib.resources/indicator/dtos/indicator';
import { ProfilesDTO } from '@lib.resources/profile/dtos/profiles';
import { newProfileOverviewFromDTO, ProfileInstanceDTO } from '@lib.resources/profile/dtos/profile-instance';
import { newProfileAspectCollectionFromDTO, ProfileAspectCollectionDTO } from '@lib.resources/profile/dtos/profile-aspect';

@Injectable({
  providedIn: 'root'
})
export class ProfileApiService implements ProfileResource {
  private resourceRegistry = inject(ResourceRegistry);
  private http = inject(HttpClient);
  private envResource$: Observable<EnvironmentResource> = this.resourceRegistry.getEnvironmentResource();

  getProfilesOverview(clientId: string, locationId: string, isFeatured = false): Observable<ProfileOverview[]> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/profile-instances${isFeatured ? '/featured' : ''}`;
        const httpParams = getHttpParams({ clientSubdomain: clientId, locationId });
        return this.http.get<ProfilesDTO>(url, { params: httpParams });
      }),
      map((profilesDTO: ProfilesDTO) => profilesDTO.items.map((profile) => newProfileOverviewFromDTO(profile)))
    );
  }

  getProfileOverview(profileHashKey: string, clientId: string): Observable<ProfileOverview | undefined> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/profile-instances/hash-keys/${profileHashKey}`;
        const httpParams = getHttpParams({
          clientSubdomain: clientId,
        });
        return this.http.get<ProfileInstanceDTO>(url, { params: httpParams });
      }),
      map((profileDto: ProfileInstanceDTO) => newProfileOverviewFromDTO(profileDto))
    );
  }

  getProfileAspectCollection(profileHashKey: string, clientId: string): Observable<AspectCollection> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${profileHashKey}`;
        const httpParams = getHttpParams({
          clientSubdomain: clientId,
        });
        return this.http.get<AspectCollectionDTO>(url, { params: httpParams });
      }),
      map((aspectCollectionDTO) => (
        newAspectCollectionFromDto(aspectCollectionDTO)
      )),
    );
  }

  getProfileOverviewByNameId(profileNameId: string, locationId: string, clientId: string): Observable<ProfileOverview> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/profile-instances/name/${encodeURIComponent(profileNameId)}`;
        const httpParams = getHttpParams({ locationId, clientSubdomain: clientId });
        return this.http.get<ProfileInstanceDTO>(url, { params: httpParams });
      }),
      map((profileDto: ProfileInstanceDTO) => newProfileOverviewFromDTO(profileDto))
    );
  }

  getProfileAspectsByNameId(profileNameId: string, locationId: string, clientId: string): Observable<ProfileAspectCollection> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances`;
        const httpParams = getHttpParams({ profileNameId, locationId, clientSubdomain: clientId });
        return this.http.get<ProfileAspectCollectionDTO>(url, { params: httpParams });
      }),
      map((collectionDTO: ProfileAspectCollectionDTO) => newProfileAspectCollectionFromDTO(collectionDTO)),
    );
  }
}
